/**
 * T-65 (PO 01.10.2026): „Löschen button wie in outlook bei hovern da wo datei ist
 * mit anzeigen". Wann darf eine Mail-Zeile den Papierkorb-Knopf beim Hovern zeigen?
 *
 * Dieselben Gründe wie beim Kontextmenü (`hideDelete` in MailView) und bei der
 * Wischgeste (`SwipeToDeleteRow enabled`):
 *  · nicht auf Touch-Geräten — dort gibt es kein Hovern, das Handy hat die
 *    Wischgeste;
 *  · nicht im Papierkorb — dort wäre „in den Papierkorb verschieben" ein No-Op;
 *  · nicht bei Snapshot-Zeilen (Vorgangs-Filter) — die sind keine echten
 *    Graph-Nachrichten dieses Ordners;
 *  · nur mit bekanntem Postfach — sonst wüsste der Verschiebe-Aufruf nicht, wohin.
 */
export function zeigeHoverLoeschen(z: {
    touch: boolean;
    activeFolderId: string | null | undefined;
    snapshotOnly?: boolean;
    mailbox: string | null | undefined;
}): boolean {
    return !z.touch && z.activeFolderId !== "deleteditems" && z.snapshotOnly !== true && !!z.mailbox;
}

/**
 * T-231: Im Ordner „Entwürfe" löscht die Liste über den Entwurfs-Weg
 * (`DELETE /mail/drafts/:id`), der sich über den Entwurfs-Bus meldet — dann
 * ziehen Entwurfs-Index und offene Fenster nach. Nur mit Senderecht (dieselbe
 * Bedingung wie das Öffnen zum Weiterschreiben) und nicht für Suchtreffer aus
 * fremden Postfächern; sonst bleibt der Move nach „Gelöschte Elemente".
 */
export function istEntwurfsLoeschen(z: {
    activeFolderId: string | null | undefined;
    srcMailbox?: string | null;
    darfSenden: boolean;
}): boolean {
    return z.activeFolderId === "drafts" && !z.srcMailbox && z.darfSenden;
}

/** T-231: Die Mail gibt es unter dieser Id nicht mehr (404). */
export function istNichtMehrVorhanden(err: unknown): boolean {
    const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
    return /^\s*404\b/.test(msg);
}

export const MAIL_SCHON_WEG_TEXT =
    "Die Mail ist nicht mehr vorhanden – vermutlich wurde sie schon gelöscht, gesendet oder verschoben. Die Liste wurde aktualisiert.";

export type LoeschErgebnis =
    | { art: "papierkorb"; neueId: string | null }
    | { art: "entwurf" }
    | { art: "schon-weg" };

/**
 * T-231: Eine Zeile löschen. Ein 404 heißt „schon weg" (z. B. im Entwurfsfenster
 * gesendet/gelöscht, oder in Outlook) — kein Fehler, die Zeile muss nur raus.
 * Gilt bewusst NUR fürs Löschen, nicht für Verschieben/Archivieren.
 */
export async function loescheMailZeile(
    a: { mailbox: string; id: string; entwurf: boolean },
    deps: {
        moveMessage: (mailbox: string, id: string, ziel: string) => Promise<{ id?: string | null } | null | undefined>;
        deleteDraft: (mailbox: string, id: string) => Promise<void>;
    },
): Promise<LoeschErgebnis> {
    try {
        if (a.entwurf) {
            await deps.deleteDraft(a.mailbox, a.id);
            return { art: "entwurf" };
        }
        const res = await deps.moveMessage(a.mailbox, a.id, "deleteditems");
        return { art: "papierkorb", neueId: res?.id ?? null };
    } catch (err) {
        if (istNichtMehrVorhanden(err)) return { art: "schon-weg" };
        throw err;
    }
}
